import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";

const useStyles = makeStyles((theme) => {
  const w = theme.spacing(30);
  const createT = theme.transitions.create;
  const sharpT = theme.transitions.easing.sharp;
  const enterT = theme.transitions.duration.enteringScreen;
  const leaveT = theme.transitions.duration.leavingScreen;

  return {
    // Cor do texto adaptada ao navbar BRANCO (antes era branca pra azul).
    input: {
      color: "#1a3a47",
      fontSize: "0.9rem",
      fontWeight: 500,
      overflow: "hidden",
      "& input::placeholder": {
        color: "rgba(96, 125, 139, 0.65)",
        opacity: 1,
      },
    },
    underline: {
      borderBottom: "1px solid rgba(0, 128, 173, 0.20)",
      "&:after": {
        borderBottom: "2px solid #0080ad",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottom: "1px solid rgba(0, 128, 173, 0.40)",
      },
    },
    show: {
      width: w,
      whiteSpace: "nowrap",
      transition: createT("width", {
        duration: enterT,
        easing: sharpT,
      }),
    },
    hide: {
      width: 0,
      overflowX: "hidden",
      transition: createT("width", {
        duration: leaveT,
        easing: sharpT,
      }),
    },
  };
});

function DynamicSearchField(props) {
  const { searchFieldOpen, setSearchFieldOpen, dynamicSearchField } = props;
  // Estado LOCAL — antes era no Redux storage (compartilhado com a Table),
  // o que causava DOIS filtros simultâneos: o `dynamicSearchField` no banco +
  // a Table re-filtrando localmente via `filterBySearchTerm(searchTerm)`.
  // Agora a Table não vê esse valor, então só a busca no banco rola.
  const [searchTerm, setSearchTerm] = React.useState('');
  const inputRef = React.useRef(null);
  const classes = useStyles();

  // Auto-foca o input quando o campo abre. `autoFocus` no Input só dispara
  // no mount — não quando o searchFieldOpen muda. Como o Input vive escondido
  // (width: 0) até abrir, precisamos forçar o foco no momento do toggle.
  React.useEffect(() => {
    if (searchFieldOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchFieldOpen]);

  // DEBOUNCE — dispara a busca no backend 400ms depois que o usuário parar
  // de digitar. Só observa `searchTerm` (não `searchFieldOpen`) — assim
  // ABRIR o campo (que só muda searchFieldOpen, mantendo searchTerm vazio)
  // NÃO dispara fetch desnecessário. Pesquisa só roda quando o usuário
  // realmente digita ou limpa o que tinha digitado.
  //
  // `isFirstRun` evita disparar no mount inicial (searchTerm já era '').
  // Cleanup cancela buscas anteriores quando o usuário digita mais rápido
  // que o debounce — só a última digitação dispara fetch.
  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return undefined;
    }
    const handle = window.setTimeout(() => {
      if (typeof dynamicSearchField === 'function') {
        dynamicSearchField(searchTerm || null);
      }
    }, 400);
    return () => window.clearTimeout(handle);
  }, [searchTerm]);

  const performAction = () => {
    setSearchFieldOpen(true);
    inputRef.current.focus();
    dynamicSearchField(searchTerm);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      performAction();
    }
  };

  return (
    <>
      {!searchFieldOpen && (
        <IconButton
          onClick={() => {
            setSearchFieldOpen(true);
            inputRef.current.focus();
          }}
          size="large"
          aria-label="Buscar"
          sx={{
            color: '#0080ad',
            bgcolor: 'rgba(0, 128, 173, 0.08)',
            borderRadius: '10px',
            p: 1,
            ml: 0.5,
            '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.14)' },
          }}
        >
          <SearchIcon />
        </IconButton>
      )}
      <Input
        value={searchTerm}
        inputRef={inputRef}
        onKeyDown={handleKeyDown}
        classes={{ underline: classes.underline }}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ ml: searchFieldOpen ? { xs: 1, md: 1.5 } : 0 }}
        className={classNames(classes.input, classes.show, !searchFieldOpen && classes.hide)}
        onBlur={() => {
          if (!searchTerm) {
            setSearchFieldOpen(false);
          }
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="Fechar busca rápida"
              onClick={() => {
                setSearchFieldOpen(false);
                setSearchTerm("");
                dynamicSearchField(null);
              }}
              size="small"
              sx={{
                color: '#0080ad',
                p: 0.5,
                '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.10)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />
    </>
  );
}

DynamicSearchField.propTypes = {
  searchFieldOpen: PropTypes.bool.isRequired,
  setSearchFieldOpen: PropTypes.func.isRequired,
  dynamicSearchField: PropTypes.func.isRequired,
};

export default DynamicSearchField;
